import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'antd-mobile';
import { CloseOutline, CheckCircleFill, CloseCircleFill } from 'antd-mobile-icons';
import { Pda } from './components/Pda';
import { Header } from './components/Header';
import { ArrivalRelatedScanComponent, ArrivalRelatedScanComponentRef, HandleScanEventResult } from './components/ArrivalRelatedScanComponent';
import { isValidBarCode, showBarCodeError } from '../../utils/BarCodeUtil';
import { t } from '../../i18n';
import { parseException } from '../../utils/exceptionUtils';

const POPUP_TYPE_INTERCEPT = 1;
const POPUP_TYPE_NEED_ACTION = 2;
const POPUP_TYPE_INTERCEPT_NO_POP = 5;
const POPUP_TYPE_NEED_ACTION_NO_POP = 6;

interface PromptInfoGql {
    messageKey: string;
    popupType: number;
    content: string;
    title: string;
    actionButtonText?: string | null;
}

interface ReturnArrivalScanResponseGql {
    success: boolean;
    disposition?: string | null;
    returnSortingCode?: string | null;
    promptInfo?: PromptInfoGql | null;
    trackingNumber?: string | null;
    orderStatusLabel?: string | null;
    nextContent?: string | null;
    nextTitle?: string | null;
    actionButtonText?: string | null;
}

const HANDLE_RETURN_ARRIVAL_SCAN_EVENT = gql`
    mutation HandleReturnArrivalScanEventV2($barCode: String!) {
        handleReturnArrivalScanEventV2(barCode: $barCode) {
            success
            disposition
            returnSortingCode
            trackingNumber
            orderStatusLabel
            nextContent
            nextTitle
            actionButtonText
            promptInfo {
                messageKey
                popupType
                content
                title
            }
        }
    }
`;

const INITIATE_RETURN_AND_ARRIVAL_SCAN = gql`
    mutation ManualReturnAndReturnArrivalScan($barCode: String!) {
        manualReturnAndReturnArrivalScan(barCode: $barCode) {
            success
            disposition
            returnSortingCode
            trackingNumber
            orderStatusLabel
            promptInfo {
                messageKey
                popupType
                content
                title
            }
        }
    }
`;

type DispositionState = {
    disposition: string;
    returnSortingCode?: string | null;
    trackingNumber: string;
    originalBarCode: string;
    isFromInitiate: boolean;
    orderStatusLabel?: string | null;
    pendingPromptInfo?: PromptPopupState | null;
};

type PromptPopupState = {
    content: string;
    title: string;
    barCode: string;
    popupType: number;
};

type NeedActionOverlayState = {
    content: string;
    title: string;
    actionButtonText: string;
    barCode: string;
    originalBarCode: string;
    orderStatusLabel?: string | null;
};

const DispositionOverlay = ({
    data,
    onClose,
}: {
    data: DispositionState;
    onClose: () => void;
}) => {
    const isReturn = data.disposition === 'RETURN';
    const [showBorder, setShowBorder] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setShowBorder(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    const statusText = data.orderStatusLabel
        ? t(`enum.overallStatus.${data.orderStatusLabel}`)
        : '-';

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 px-6 bg-white"
        >
            {showBorder && <div className="absolute inset-0 border-8 border-green-500 pointer-events-none" />}
            <button
                type="button"
                className="absolute top-4 right-4 text-gray-400"
                onClick={onClose}
            >
                <CloseOutline fontSize={24} />
            </button>
            <div className="text-sm font-bold text-center flex items-center justify-center gap-1 text-green-500">
                {t('pda.pages.returnArrival.arrivalSuccess')} <CheckCircleFill color="#00b578" style={{ fontSize: '20px' }} />
            </div>
            <div className="text-black text-2xl font-bold text-center">
                {isReturn ? t('pda.pages.returnArrival.nextStepReturn') : t('pda.pages.returnArrival.nextStepDestroy')}
            </div>
            {isReturn && data.returnSortingCode && (
                <>
                    <div className="text-gray-500 text-sm text-center">
                        {t('pda.pages.returnArrival.sortingCode')}
                    </div>
                    <div className="text-black text-2xl font-bold text-center">
                        {data.returnSortingCode}
                    </div>
                </>
            )}
            <div className="w-full max-w-xs" onClick={e => e.stopPropagation()}>
                <div className="text-black text-sm font-bold mb-2">
                    {t('pda.pages.returnArrival.parcelDetailSection')}
                </div>
                <table className="w-full text-xs border-collapse border border-gray-500">
                    <tbody>
                        <tr>
                            <td className="py-2 px-3 text-gray-500 w-2/5 border border-gray-500">{t('pda.pages.returnArrival.trackingNumberLabel')}</td>
                            <td className="py-2 px-3 text-black break-all border border-gray-500">{data.trackingNumber}</td>
                        </tr>
                        <tr>
                            <td className="py-2 px-3 text-gray-500 border border-gray-500">{t('pda.pages.returnArrival.parcelStatusLabel')}</td>
                            <td className="py-2 px-3 text-black border border-gray-500">{statusText}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const NeedActionOverlay = ({
    data,
    onClose,
    onAction,
    actionLoading,
}: {
    data: NeedActionOverlayState;
    onClose: () => void;
    onAction: () => void;
    actionLoading: boolean;
}) => {
    const [showBorder, setShowBorder] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setShowBorder(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    const handleActionClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const confirmed = await Dialog.confirm({
            content: t('pda.pages.returnArrival.initiateReturnConfirm'),
            confirmText: t('pda.common.continueButton'),
            cancelText: t('pda.common.back'),
        });
        if (confirmed) {
            onAction();
        }
    };

    const statusText = data.orderStatusLabel
        ? t(`enum.overallStatus.${data.orderStatusLabel}`)
        : '-';

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 px-6 bg-white"
        >
            {showBorder && <div className="absolute inset-0 border-8 border-orange-500 pointer-events-none" />}
            <button
                type="button"
                className="absolute top-4 right-4 text-gray-400"
                onClick={onClose}
            >
                <CloseOutline fontSize={24} />
            </button>
            {data.title && <div className="text-red-500 text-sm font-bold text-center flex items-center justify-center gap-1">{data.title} <CloseCircleFill color="var(--adm-color-danger)" style={{ fontSize: '20px' }} /></div>}
            {data.content && <div className="text-black text-2xl font-bold text-center whitespace-pre-line">{data.content.replace(/\\n/g, '\n')}</div>}
            <button
                type="button"
                className="w-full max-w-xs bg-blue-500 text-white py-3 rounded-lg text-base font-medium disabled:opacity-50"
                onClick={handleActionClick}
                disabled={actionLoading}
            >
                {actionLoading ? t('pda.common.submitting') : data.actionButtonText}
            </button>
            <div className="w-full max-w-xs" onClick={e => e.stopPropagation()}>
                <div className="text-black text-sm font-bold mb-2">
                    {t('pda.pages.returnArrival.parcelDetailSection')}
                </div>
                <table className="w-full text-xs border-collapse border border-gray-500">
                    <tbody>
                        <tr>
                            <td className="py-2 px-3 text-gray-500 w-2/5 border border-gray-500">{t('pda.pages.returnArrival.trackingNumberLabel')}</td>
                            <td className="py-2 px-3 text-black break-all border border-gray-500">{data.barCode}</td>
                        </tr>
                        <tr>
                            <td className="py-2 px-3 text-gray-500 border border-gray-500">{t('pda.pages.returnArrival.parcelStatusLabel')}</td>
                            <td className="py-2 px-3 text-black border border-gray-500">{statusText}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const PromptPopup = ({
    data,
    onClose,
}: {
    data: PromptPopupState;
    onClose: () => void;
}) => {
    const tail = data.barCode ? data.barCode.slice(-6) : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" aria-label="return-arrival-prompt" onClick={onClose}>
            <div className="absolute inset-0 border-8 border-red-500 pointer-events-none" />
            <div className="w-full max-w-xs bg-red-500 text-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
                    <div className="relative flex items-center justify-center px-4 py-2">
                        <div className="text-sm font-medium opacity-90">⊗ {t('pda.pages.returnArrival.operationFailed')}</div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="close-prompt"
                            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 opacity-90"
                        >
                            <CloseOutline fontSize={18} />
                        </button>
                    </div>
                    <div className="px-4 py-4 text-center break-words text-white">
                        {tail && (
                            <>
                                <div className="text-sm opacity-80 mb-1">{t('pda.scan.barCodeTailLabel')}</div>
                                <div className="text-3xl font-bold mb-2 tracking-wider">{tail}</div>
                            </>
                        )}
                        {data.title && <div className="text-3xl font-bold mb-2">{data.title}</div>}
                        {data.content && <div className="text-sm opacity-90 whitespace-pre-line text-center">{data.content.replace(/\\n/g, '\n')}</div>}
                    </div>
            </div>
        </div>
    );
};

const PageBody = () => {

    const compRef = useRef<ArrivalRelatedScanComponentRef>(null);
    const [dispositionOverlay, setDispositionOverlay] = useState<DispositionState | null>(null);
    const [needActionOverlay, setNeedActionOverlay] = useState<NeedActionOverlayState | null>(null);
    const [promptPopup, setPromptPopup] = useState<PromptPopupState | null>(null);
    const [initiateLoading, setInitiateLoading] = useState(false);
    const [scanResultKey, setScanResultKey] = useState(0);

    const [handleReturnArrivalScanEvent] = useMutation<{ handleReturnArrivalScanEventV2: ReturnArrivalScanResponseGql }>(HANDLE_RETURN_ARRIVAL_SCAN_EVENT);
    const [initiateReturnAndArrivalScan] = useMutation<{ manualReturnAndReturnArrivalScan: ReturnArrivalScanResponseGql }>(INITIATE_RETURN_AND_ARRIVAL_SCAN);

    const showDispositionOverlay = (resp: ReturnArrivalScanResponseGql, scannedBarCode: string, isFromInitiate: boolean, originalBarCode?: string) => {
        setScanResultKey(k => k + 1);
        setNeedActionOverlay(null);
        setPromptPopup(null);
        const trackingNumber = resp.trackingNumber ?? scannedBarCode;
        setDispositionOverlay({
            disposition: resp.disposition!,
            returnSortingCode: resp.returnSortingCode,
            trackingNumber,
            originalBarCode: originalBarCode ?? scannedBarCode,
            isFromInitiate,
            orderStatusLabel: resp.orderStatusLabel,
            pendingPromptInfo: (resp.promptInfo && resp.promptInfo.popupType > 0 && resp.promptInfo.popupType < POPUP_TYPE_INTERCEPT_NO_POP) ? {
                content: resp.promptInfo.content,
                title: resp.promptInfo.title,
                barCode: trackingNumber,
                popupType: resp.promptInfo.popupType,
            } : null,
        });
    };

    const handleDispositionClose = () => {
        if (dispositionOverlay?.isFromInitiate) {
            compRef.current?.moveToSuccess(dispositionOverlay.originalBarCode, {
                disposition: dispositionOverlay.disposition,
                returnSortingCode: dispositionOverlay.returnSortingCode,
            });
        }
        const pendingPrompt = dispositionOverlay?.pendingPromptInfo;
        setDispositionOverlay(null);
        if (pendingPrompt) {
            setPromptPopup(pendingPrompt);
        }
    };

    const handleInitiateReturn = async () => {
        if (!needActionOverlay) return;
        const barCode = needActionOverlay.barCode;
        const originalBarCode = needActionOverlay.originalBarCode;
        setInitiateLoading(true);
        try {
            const { data } = await initiateReturnAndArrivalScan({ variables: { barCode } });
            const resp = data?.manualReturnAndReturnArrivalScan;
            if (resp?.success && resp.disposition) {
                setNeedActionOverlay(null);
                showDispositionOverlay(resp, barCode, true, originalBarCode);
            } else {
                const errorContent = resp?.promptInfo?.content || t('pda.common.submitFailed');
                setNeedActionOverlay(null);
                setPromptPopup({ content: errorContent, title: resp?.promptInfo?.title || '', barCode, popupType: POPUP_TYPE_INTERCEPT });
            }
        } catch (error) {
            const { message } = parseException(error);
            setNeedActionOverlay(null);
            setPromptPopup({ content: message, title: '', barCode, popupType: POPUP_TYPE_INTERCEPT });
        } finally {
            setInitiateLoading(false);
        }
    };

    const handleScanEvent = async (barCode: string): Promise<HandleScanEventResult> => {
        if (!isValidBarCode(barCode)) {
            showBarCodeError(barCode);
            return { success: false };
        }

        let resp: ReturnArrivalScanResponseGql | undefined;
        try {
            const { data } = await handleReturnArrivalScanEvent({ variables: { barCode } });
            resp = data?.handleReturnArrivalScanEventV2;
        } catch (error) {
            const { message } = parseException(error);
            setScanResultKey(k => k + 1);
            setDispositionOverlay(null);
            setNeedActionOverlay(null);
            setPromptPopup({ content: message, title: '', barCode, popupType: POPUP_TYPE_INTERCEPT });
            const err = new Error(message);
            (err as any).customMessage = message;
            throw err;
        }

        if (resp?.success && resp.disposition) {
            showDispositionOverlay(resp, barCode, false);
            return { success: true, disposition: resp.disposition, returnSortingCode: resp.returnSortingCode } as any;
        }

        const promptInfo = resp?.promptInfo;
        if (promptInfo?.popupType === POPUP_TYPE_NEED_ACTION_NO_POP) {
            const needActionContent = resp?.nextContent ?? '';
            setScanResultKey(k => k + 1);
            setDispositionOverlay(null);
            setPromptPopup(null);
            setNeedActionOverlay({
                content: needActionContent,
                title: resp?.nextTitle ?? '',
                actionButtonText: resp?.actionButtonText ?? '',
                barCode: resp?.trackingNumber ?? barCode,
                originalBarCode: barCode,
                orderStatusLabel: resp?.orderStatusLabel,
            });
            const error = new Error(needActionContent);
            (error as any).customMessage = needActionContent;
            throw error;
        }
        if (promptInfo?.popupType === POPUP_TYPE_INTERCEPT) {
            setScanResultKey(k => k + 1);
            setDispositionOverlay(null);
            setNeedActionOverlay(null);
            setPromptPopup({
                content: promptInfo.content,
                title: promptInfo.title,
                barCode: resp?.trackingNumber ?? barCode,
                popupType: POPUP_TYPE_INTERCEPT,
            });
            const error = new Error(promptInfo.content);
            (error as any).customMessage = promptInfo.content;
            throw error;
        }

        return { success: false };
    };

    return (
        <>
            <ArrivalRelatedScanComponent
                ref={compRef}
                value1Placeholder={t('pda.pages.returnArrival.bagCodeTrackingNo')}
                value1ColumnTitle={t('pda.common.bagPackage')}
                handleScanEvent={handleScanEvent}
                successExtraFields={[
                    { title: t('pda.pages.returnArrival.disposition'), key: 'disposition' },
                    { title: t('pda.pages.returnArrival.sortingCode'), key: 'returnSortingCode' },
                ]}
            />
            {dispositionOverlay && (
                <DispositionOverlay key={scanResultKey} data={dispositionOverlay} onClose={handleDispositionClose} />
            )}
            {needActionOverlay && (
                <NeedActionOverlay
                    key={scanResultKey}
                    data={needActionOverlay}
                    onClose={() => setNeedActionOverlay(null)}
                    onAction={handleInitiateReturn}
                    actionLoading={initiateLoading}
                />
            )}
            {promptPopup && (
                <PromptPopup
                    key={scanResultKey}
                    data={promptPopup}
                    onClose={() => setPromptPopup(null)}
                />
            )}
        </>
    );
};

export default function ReturnArrivalPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.returnArrival.returnArrivalScan')}/>
            <Pda/>
            <PageBody/>
        </div>
    );
}
